import React, { useEffect, useState, useRef } from 'react';
import { AutoComplete, Input, Spin } from 'antd';
import { CloseCircleFilled, CloseOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { CSSTransition } from 'react-transition-group';
import { fetchLocations } from '../services/apiService';
import languages from '../services/languageConfig';
import globalIcon from '../assets/global.png';
import directionIcon from '../assets/directions_v2.png';
import { Logo } from '../../constants/commonConstant';

const Navbar = ({ onSearch, onLanguageChange, resetPage }) => {
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const searchPanelRef = useRef(null);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        setLoading(true);
        const locations = await fetchLocations();
        setLocations(locations);
      } catch (error) {
        console.error('Error fetching location data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLocationData();
  }, []);

  const handleLanguageToggle = () => {
    const newLanguage = language === 'en' ? 'lk' : 'en';
    setLanguage(newLanguage);
    setSearchText('');
    setSelectedLocation('');
    setOptions([]);
    onLanguageChange && onLanguageChange(newLanguage);
    resetPage && resetPage();
  };

  const handleSearch = (value) => {
    setSearchText(value);

    if (!value.trim()) {
      handleClearSearch();
      return;
    }

    const filteredOptions = locations
      .filter((location) => {
        return Object.keys(location).some((key) => {
          return (
            (key.startsWith('name_') || key.startsWith('province_')) &&
            location[key]?.toLowerCase().includes(value.toLowerCase())
          );
        });
      })
      .map((location) => {
        const name =
          location[`name_${language}`] ||
          Object.keys(location)
            .filter((key) => key.startsWith('name_'))
            .map((key) => location[key])[0] || 'Unknown';

        const province =
          location[`province_${language}`] ||
          Object.keys(location)
            .filter((key) => key.startsWith('province_'))
            .map((key) => location[key])[0] || '';

        return {
          value: name,
          label: (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={directionIcon} alt="icon" style={{ width: '12px', height: '12px' }} />
                <span style={{ fontSize: '14px', fontFamily: languages[language]?.fontFamily }}>{name}</span>
              </div>
              <span
                style={{
                  fontSize: '12px',
                  color: '#aaa',
                  marginLeft: '22px',
                  fontFamily: languages[language]?.fontFamily,
                }}
              >
                {province}
              </span>
            </div>
          ),
        };
      });

    setOptions(filteredOptions);
  };

  const handleSelect = (value) => {
    setSelectedLocation(value);
    setSearchText(value);
    setOptions([]);
    onSearch && onSearch(value);
  };

  const handleClearSearch = () => {
    setSearchText('');
    setSelectedLocation('');
    setOptions([]);
    onSearch && onSearch('');
    resetPage && resetPage();
  };

  const toggleSearchVisibility = () => {
    setIsSearchVisible((prev) => {
      if (!prev) {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      return !prev;
    });
  };

  return (
    <div
      style={{
        position: isSearchVisible ? 'relative' : 'sticky',
        top: 0,
        zIndex: 1000,
        width: '100%',
        backgroundColor: 'black',
        padding: '20px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '90%',
          maxWidth: '500px',
          margin: '0 auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src={globalIcon}
            alt="Language Toggle"
            style={{ width: '24px', height: '24px', cursor: 'pointer' }}
            onClick={handleLanguageToggle}
          />
          <span
            style={{
              color: 'white',
              fontSize: languages[language].fontSize,
              fontFamily: languages[language].fontFamily,
              cursor: 'pointer',
            }}
            onClick={handleLanguageToggle}
          >
            {languages[language].label}
          </span>
        </div>

        <div
          style={{
            textAlign: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <span
            style={{
              fontSize: '40px',
              color: 'white',
              fontFamily: 'Bauhaus_93',
            }}
          >
            {Logo}
          </span>
        </div>

        <div>
          {isSearchVisible ? (
            <CloseOutlined
              style={{
                fontSize: '24px',
                color: 'white',
                cursor: 'pointer',
              }}
              onClick={toggleSearchVisibility}
            />
          ) : (
            <SearchOutlined
              style={{
                fontSize: '24px',
                color: 'white',
                cursor: 'pointer',
              }}
              onClick={toggleSearchVisibility}
            />
          )}
        </div>
      </div>

      <CSSTransition in={isSearchVisible} timeout={300} classNames="search-panel" unmountOnExit nodeRef={searchPanelRef}>
        <div
          ref={searchPanelRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '40px auto 0',
            width: '90%',
            maxWidth: '500px',
          }}
        >
          <AutoComplete
            options={options}
            style={{ width: '100%', height: '50px' }}
            value={searchText}
            onSearch={handleSearch}
            onSelect={handleSelect}
          >
            <Input
              prefix={<SearchOutlined style={{ color: 'white', fontSize: '18px', paddingRight: '6px' }} />}
              suffix={
                loading ? (
                  <Spin indicator={<LoadingOutlined style={{ fontSize: '16px', color: 'white' }} />} />
                ) : searchText ? (
                  <CloseCircleFilled
                    style={{ color: 'white', fontSize: '12px', cursor: 'pointer' }}
                    onClick={handleClearSearch}
                  />
                ) : null
              }
              style={{
                height: '50px',
                borderRadius: '20px',
                backgroundColor: '#363636',
                color: 'white',
                border: 'none',
                outline: 'none',
                fontSize: '16px',
                fontFamily: languages[language].fontFamily,
              }}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={languages[language].placeholder}
              className={`search-input-${languages[language].code}`}
            />
          </AutoComplete>

          {selectedLocation && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                border: '2px solid white',
                overflow: 'hidden',
                padding: '0 8px',
                height: '32px',
                marginTop: '10px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              <span style={{ fontSize: '14px', marginRight: '6px', fontFamily: languages[language].fontFamily }}>
                {selectedLocation}
              </span>
              <CloseOutlined style={{ fontSize: '14px', cursor: 'pointer' }} onClick={handleClearSearch} />
            </div>
          )}
        </div>
      </CSSTransition>
    </div>
  );
};

export default Navbar;